// @flow
import { type Node, useMemo, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { find, propSatisfies, complement, is, map } from 'ramda';

import SimpleDate from 'app/common/classes/simple-date';
import { api } from 'app/api';
import { GET_CAFETERIA_CUSTOMER_ORDERS_SALES, convertParams, convertResult } from 'app/common/api/requests/cafeteria/customer/orders/sales.get';

import exchangeComplexErrorProcessing from './exchangeComplexErrorProcessing';


type Props = {
  children: ({
    header: string,
    successMessage: string,
    requestOrderInitialItems: () => Promise<Array<{ id: string, amount: number }> | null>,
    errorProcessing: (Error) => string,
  }) => Node,
  foodModeTitle: string,
  date: SimpleDate,
  customerId: string,
  orderId: string,
}

const ReplaceComplex = ({ children, foodModeTitle, date, customerId, orderId }: Props): Node => {
  const { t } = useTranslation('cafeteria');
  const dateTitle = date.getString('DD.MM.YYYY');

  const header = t('order_request_sales_exchange_complex_header', {
    foodMode: foodModeTitle,
    date: dateTitle,
  });

  const successMessage = t('order_request_sales_exchange_complex_success_message', {
    foodMode: foodModeTitle,
    date: dateTitle,
  });

  const errorProcessing = useMemo(() => (
    exchangeComplexErrorProcessing(foodModeTitle, dateTitle)
  ), [foodModeTitle, dateTitle]);

  const requestOrderInitialItems = useCallback(async () => {
    const res = await api.request(GET_CAFETERIA_CUSTOMER_ORDERS_SALES, {
      error: t(
        'order_request_sales_exchange_complex_fetch_error',
        'Не удалось получить данные заявки для замены комплектов',
      ),
      params: {
        customerId,
        orderId,
      },
      convertions: {
        convertParams,
        convertResult,
      },
    });

    if (res && !find(propSatisfies(complement(is(Number)), 'amount'), res)) {
      return map(({ id, amount }) => ({ id, amount: amount || 0 }), res);
    }
    return null;
  }, [customerId, orderId, t]);


  return (
    children({
      header,
      successMessage,
      requestOrderInitialItems,
      errorProcessing,
    })
  );
};

export default ReplaceComplex;
